import { ComponentProps, ReactNode } from 'react';
import clsx from 'clsx';

import { formatPrice } from '../../utils/format';

import styles from './ProductCard.module.css';

interface ProductCardProps extends Omit<ComponentProps<'div'>, 'title'> {
  price?: number | ReactNode;
  priceNote?: ReactNode;
  priceLabel?: ReactNode;
  title?: ReactNode;
  titleSuffix?: ReactNode;
  coupon?: string;
  couponPrice?: number;
  couponLabel?: ReactNode;
  cta?: ReactNode;
  description?: ReactNode;
  selected?: boolean;
  loading?: boolean;
  id?: string;
}

export default function ProductCard({
  price,
  priceNote,
  priceLabel,
  title,
  titleSuffix,
  description,
  cta,
  className,
  onClick,
  selected,
  loading,
  coupon,
  couponPrice,
  couponLabel,
  ...props
}: ProductCardProps) {
  const clickable = !!onClick;

  if (loading) {
    return (
      <div className={clsx(styles.card, styles.loading, className)}>
        <div className={styles.titleLine}>
          <h4 className={styles.title}>Loading...</h4>
        </div>
        <div className={styles.pricingSection}>
          <div className={styles.priceLine}>
            <span className={styles.price}>Loading</span>
          </div>
          <p className={styles.priceNote}>Loading</p>
          <div className={styles.cta}>Loading...</div>
        </div>

        <div className={styles.description}>Loading...</div>
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.card, selected && styles.selected, clickable && styles.clickable, className)}
      onClick={onClick}
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? 'button' : undefined}
      aria-selected={selected}
      itemScope
      itemType="https://schema.org/Product"
      {...props}
    >
      <div className={styles.titleLine}>
        <h4
          className={styles.title}
          itemProp="name"
        >
          {title}
        </h4>
        {titleSuffix}
      </div>

      <div
        className={styles.pricingSection}
        itemProp="offers"
        itemScope
        itemType="https://schema.org/Offer"
      >
        <div className={styles.priceLine}>
          <span className={styles.price}>{typeof price === 'number' ? formatPrice(price) : price}</span>
          {priceLabel && <span className={styles.priceLabel}>{priceLabel}</span>}
        </div>

        {couponLabel && <p className={styles.couponLabel}>{couponLabel}</p>}

        {priceNote && <p className={styles.priceNote}>{priceNote}</p>}

        {coupon && (
          <span className="sr-only">
            <span itemProp="name">{titleSuffix || coupon}</span>
            <meta
              itemProp="discountCode"
              content={coupon}
            />
            {couponPrice && formatPrice(couponPrice, 'discount')}
          </span>
        )}

        {cta ? <div className={styles.cta}>{cta}</div> : null}
      </div>

      <div
        className={styles.description}
        itemProp="description"
      >
        {description}
      </div>
    </div>
  );
}
